import type { PaginatedResponse } from '@/entities/pagination/paginated-response';
import type { InfiniteData, QueryClient, QueryKey } from '@tanstack/vue-query';

export type QueriesDataType<T> = InfiniteData<PaginatedResponse<T>> | T[] | T;

export const updateItemOnQueriesData = <T extends { id: string | number }>(
  queryKey: QueryKey,
  result: T,
  queryClient: QueryClient,
) => {
  const oldQueryData = queryClient.getQueriesData<QueriesDataType<T>>({
    queryKey,
  });
  if (!oldQueryData) return;
  oldQueryData.forEach((queryData) => {
    const [key, oldData] = queryData;
    if (!oldData) return;
    if ('pages' in oldData) {
      const updatedData = {
        pageParams: oldData.pageParams,
        pages: oldData.pages.map((page) => ({
          ...page,
          items: page.items.map((item) => (item.id === result.id ? result : item)),
        })),
      };
      queryClient.setQueryData(key, () => updatedData);
    } else if (Array.isArray(oldData)) {
      queryClient.setQueryData(key, () =>
        oldData.map((item) => (item.id === result.id ? result : item)),
      );
    } else if (oldData && 'id' in oldData && result.id === oldData.id) {
      queryClient.setQueryData(key, () => result);
    }
  });
};

export const addItemToQueriesData = <T extends { id: string | number }>(
  queryKey: QueryKey,
  result: T,
  queryClient: QueryClient,
) => {
  const oldQueryData = queryClient.getQueriesData<QueriesDataType<T>>({
    queryKey,
  });
  if (!oldQueryData) return;
  oldQueryData.forEach((queryData) => {
    const [key, oldData] = queryData;
    if (!oldData) return;

    const itemExists =
      'pages' in oldData
        ? oldData.pages.some((page) => page.items.some((item) => item.id === result.id))
        : Array.isArray(oldData)
          ? oldData.some((item) => item.id === result.id)
          : false;
    if (itemExists) {
      return updateItemOnQueriesData(queryKey, result, queryClient);
    }

    if ('pages' in oldData) {
      const updatedData = {
        pageParams: oldData.pageParams,
        pages: oldData.pages.map((page, i) =>
          i === 0 ? { ...page, items: [result, ...page.items] } : page,
        ),
      };
      queryClient.setQueryData(key, () => updatedData);
    } else if (Array.isArray(oldData)) {
      oldData.unshift(result);
      queryClient.setQueryData(key, () => oldData);
    }
  });
};

export const removeItemFromQueriesData = <T extends { id: string | number }>(
  queryKey: QueryKey,
  removeId: string | undefined,
  queryClient: QueryClient,
) => {
  const oldQueryData = queryClient.getQueriesData<QueriesDataType<T>>({
    queryKey,
  });
  if (!oldQueryData) return;
  oldQueryData.forEach((queryData) => {
    const [key, oldData] = queryData;
    if (!oldData) return;
    if ('pages' in oldData) {
      const updatedData = {
        pageParams: oldData.pageParams,
        pages: oldData.pages.map((page) => ({
          ...page,
          items: page.items.filter((item) => item.id !== removeId),
        })),
      };
      queryClient.setQueryData(key, () => updatedData);
    } else if (Array.isArray(oldData)) {
      const updatedData = oldData.filter((item) => item.id !== removeId);
      queryClient.setQueryData(key, () => updatedData);
    }
  });
};
